import { Dispatch, SetStateAction } from 'react'
import { ActivityJson } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { SearchResponse } from 'app/services/search'
import { gqlMutation } from 'app/lib/graphqlClient'
import { REASSIGN_ACTIVITY_MUTATION } from 'app/stores/ActivityStore'
import { logError } from 'lib/logging'

type OwnerValue = ActivityJson['owner']

/** Optimistic owner update with rollback on failure. */
export function useActivityOwnerUpdate(
  activities: SearchResponse<ActivityJson> | undefined,
  setActivities: Dispatch<SetStateAction<SearchResponse<ActivityJson> | undefined>>,
) {
  return async (activityId: string, owner?: OwnerValue) => {
    const previousOwner = activities?.hits?.find((act) => act.id === activityId)?.owner

    // Optimistic update
    setActivities((prev) => {
      const hits = [...(prev?.hits ?? [])].map((act) => (act.id === activityId ? { ...act, owner } : act))
      return { total: prev?.total ?? 0, page: prev?.page ?? 0, hits }
    })

    const result = await gqlMutation(REASSIGN_ACTIVITY_MUTATION, {
      id: activityId,
      input: { ownerId: owner?.id ?? `-1`, ownerName: owner?.name ?? `-` },
    })
    if (result.error) {
      logError(`Error updating activity owner`, result.error.message)
      notifyError(`Error updating activity owner`)
      setActivities((prev) => {
        const hits = [...(prev?.hits ?? [])].map((act) =>
          act.id === activityId ? { ...act, owner: previousOwner } : act,
        )
        return { total: prev?.total ?? 0, page: prev?.page ?? 0, hits }
      })
      return
    }

    notifySuccess(`Staff updated successfully`)
  }
}
